import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";
import {
  Phone, PhoneCall, PhoneOff, ArrowLeft, ArrowRight, Loader2, Check, X,
  Calendar, Sparkles, ChevronRight, ChevronLeft, Package, User,
  Building2, Landmark, AlertTriangle, ClipboardList, ExternalLink, BellOff,
  Lock, CheckCircle2, Wrench, TrendingUp, ShoppingCart, ClipboardCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ProductImageOrPlaceholder } from "./Products";

// Structured response option catalogues — mirror backend constants in campaigns2.py.
const MACHINE_CONDITIONS = [
  { key: "working_perfectly",  label: "Working perfectly",     tone: "positive" },
  { key: "working_with_issues", label: "Working with issues",   tone: "warning" },
  { key: "not_working",         label: "Not working",           tone: "danger" },
  { key: "under_repair",        label: "Under repair",          tone: "warning" },
  { key: "sold_or_disposed",    label: "Sold / Disposed",       tone: "neutral" },
];
const SERVICE_REQUIREMENTS = [
  { key: "none",                label: "No service needed" },
  { key: "preventive_service",  label: "Preventive service" },
  { key: "minor_repair",        label: "Minor repair" },
  { key: "major_repair",        label: "Major repair" },
  { key: "parts_replacement",   label: "Parts replacement" },
];
const INTEREST_LEVELS = [
  { key: "not_interested",     label: "Not interested" },
  { key: "just_browsing",      label: "Just browsing" },
  { key: "considering",        label: "Considering" },
  { key: "actively_looking",   label: "Actively looking" },
  { key: "ready_to_buy",       label: "Ready to buy" },
];
const PURCHASE_TIMEFRAMES = [
  { key: "not_planning",       label: "Not planning" },
  { key: "within_month",       label: "Within a month" },
  { key: "1_to_3_months",      label: "1 – 3 months" },
  { key: "3_to_6_months",      label: "3 – 6 months" },
  { key: "beyond_6_months",    label: "Beyond 6 months" },
  { key: "undecided",          label: "Undecided" },
];

const TONE_BUTTON = {
  positive: "bg-[#39FF14]/10 border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/15",
  warning:  "bg-yellow-500/10 border-yellow-500/40 text-yellow-200 hover:bg-yellow-500/15",
  danger:   "bg-red-500/10 border-red-500/40 text-red-300 hover:bg-red-500/15",
  neutral:  "bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA] hover:bg-[#121815]",
};

const TYPE_META = {
  individual: { icon: User, chip: "bg-emerald-100 text-[#39FF14] border-emerald-200", label: "Individual" },
  business:   { icon: Building2, chip: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",  label: "Business" },
  government: { icon: Landmark,  chip: "bg-red-500/15 text-red-300 border-red-500/25",         label: "Government" },
};

const TONE_CLS = {
  positive: "bg-[#39FF14]/5 hover:bg-emerald-100 text-[#39FF14] border-[#39FF14]/25",
  warning:  "bg-yellow-500/10 hover:bg-yellow-500/15 text-yellow-200 border-yellow-500/40",
  danger:   "bg-red-500/10 hover:bg-red-500/15 text-red-300 border-red-300",
  neutral:  "bg-[#0B0F0C] hover:bg-[#121815] text-[#F8F9FA] border-[#3A4640]",
};

const LOCKED_ACTIONS = [
  { key: "send_message",   label: "Send Message",   reason: "Free-form messaging is disabled. Approved-template messaging module is coming next" },
  { key: "create_lead",    label: "Create Lead",    reason: "Leads / Marketing module not yet active" },
  { key: "create_service", label: "Create Service", reason: "Service workflow not yet integrated" },
  { key: "create_sale",    label: "Create Sale",    reason: "Sales workflow not yet integrated" },
  { key: "create_subsidy", label: "Create Subsidy", reason: "Subsidy Desk not yet integrated" },
];

function CustomerHeader({ c }) {
  const meta = TYPE_META[c.customer_type] || TYPE_META.individual;
  const Icon = meta.icon;
  const displayName = c.customer_type === "business" ? (c.organization_name || c.name)
                     : c.customer_type === "government" ? (c.department_name || c.name)
                     : c.name;
  return (
    <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-6 mb-4">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full bg-[#39FF14] text-[#0B0F0C] flex items-center justify-center shrink-0">
          <Icon size={26} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-2xl font-heading font-bold text-[#F8F9FA]">{displayName || "(no name)"}</h2>
            <span className={`text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border ${meta.chip}`}>{meta.label}</span>
            {c.incomplete && (
              <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-yellow-500/15 text-yellow-200 border-yellow-500/30 flex items-center gap-0.5">
                <AlertTriangle size={9} /> Incomplete
              </span>
            )}
            {c.opted_out && (
              <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-red-500/15 text-red-300 border-red-500/25 flex items-center gap-0.5">
                <BellOff size={9} /> Opted out
              </span>
            )}
          </div>
          <div className="text-xs font-mono text-[#8A8F98] mt-1">{c.tzat_no}</div>
          <div className="mt-2 flex items-center gap-4 flex-wrap text-sm">
            <a href={`tel:${c.phone || ""}`} className="text-[#39FF14] hover:underline flex items-center gap-1.5 font-heading text-base font-semibold" data-testid="call-primary-btn">
              <Phone size={14} /> {c.phone || "—"}
            </a>
            {c.alternate_phone && (
              <a href={`tel:${c.alternate_phone}`} className="text-[#8A8F98] hover:text-[#39FF14] flex items-center gap-1.5">
                <Phone size={13} /> Alt · {c.alternate_phone}
              </a>
            )}
          </div>
          <div className="text-xs text-[#8A8F98] mt-1.5 flex flex-wrap gap-x-3">
            {(c.village || c.district) && <span>{[c.village, c.gram_panchayat, c.block, c.district, c.state].filter(Boolean).join(" · ")}</span>}
            {c.contact_person && <span>Contact · {c.contact_person}</span>}
          </div>
        </div>
        <Link to={`/customers/${c.id}`} target="_blank" rel="noopener" className="text-xs text-[#8A8F98] hover:text-[#39FF14] flex items-center gap-1" data-testid="open-360-link">
          Customer 360 <ExternalLink size={11} />
        </Link>
      </div>
    </div>
  );
}

function MachinesPanel({ machines }) {
  if (!machines?.length) {
    return <div className="text-xs text-[#8A8F98]">No machines on record.</div>;
  }
  return (
    <ul className="space-y-2">
      {machines.map((m) => (
        <li key={m.id} className="text-sm">
          <div className="font-semibold text-[#F8F9FA]">{m.product?.brand} {m.product?.model || m.product?.name || "Machine"}</div>
          <div className="text-xs text-[#8A8F98] flex flex-wrap gap-x-3 mt-0.5">
            {m.serial_number && <span className="font-mono">S/N {m.serial_number}</span>}
            {m.chassis_number && <span className="font-mono">Chassis {m.chassis_number}</span>}
            {m.purchased_at && <span>Purchased {formatDate(m.purchased_at)}</span>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function LastInteractionPanel({ last }) {
  if (!last) return <div className="text-xs text-[#8A8F98]">No prior calls recorded.</div>;
  return (
    <div className="text-sm">
      <div className="font-semibold text-[#F8F9FA]">{last.outcome_label}</div>
      <div className="text-xs text-[#8A8F98]">{formatDate(last.called_at)} · by {last.user_name || "—"}</div>
      {last.note && <div className="text-xs text-[#8A8F98] mt-1 italic">&ldquo;{last.note}&rdquo;</div>}
    </div>
  );
}

function LastFollowupPanel({ f }) {
  if (!f) return <div className="text-xs text-[#8A8F98]">No follow-ups on record.</div>;
  return (
    <div className="text-sm">
      <div className="font-semibold text-[#F8F9FA]">{f.title}</div>
      <div className="text-xs text-[#8A8F98]">Due {formatDate(f.due_date)} · <span className="capitalize">{f.status}</span></div>
    </div>
  );
}

function EquipmentAssessmentCarousel({ machines, assessments, setAssessments, disabled }) {
  const scrollerRef = useRef(null);
  const [openIdx, setOpenIdx] = useState(null);

  const scroll = (dir) => {
    const el = scrollerRef.current; if (!el) return;
    el.scrollBy({ left: dir * 260, behavior: "smooth" });
  };

  if (!machines?.length) {
    return (
      <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5 mb-4">
        <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-2 flex items-center gap-2">
          <Wrench size={11} /> Customer Equipment
        </div>
        <div className="text-xs text-[#8A8F98]">No machines on record for this customer.</div>
      </div>
    );
  }

  const upsert = (mid, patch) => {
    setAssessments((prev) => {
      const idx = prev.findIndex((a) => a.machine_id === mid);
      if (idx === -1) {
        return [...prev, { machine_id: mid, condition: "not_assessed", service_requirements: [], notes: "", ...patch }];
      }
      const clone = [...prev];
      clone[idx] = { ...clone[idx], ...patch };
      return clone;
    });
  };
  const get = (mid) => assessments.find((a) => a.machine_id === mid) ||
    { machine_id: mid, condition: "not_assessed", service_requirements: [], notes: "" };

  // Multi-select toggle with the two clearing rules:
  //   1. Picking "none" (No service needed) removes every other selection.
  //   2. Picking any non-"none" service removes "none" if it was set.
  //   3. Clicking an already-selected service toggles it off.
  const toggleService = (mid, key) => {
    const current = get(mid).service_requirements || [];
    const has = current.includes(key);
    let next;
    if (has) {
      next = current.filter((k) => k !== key);
    } else if (key === "none") {
      next = ["none"];
    } else {
      next = [...current.filter((k) => k !== "none"), key];
    }
    upsert(mid, { service_requirements: next });
  };

  const isServiceAssessed = (a) => (a.service_requirements || []).some((r) => r && r !== "not_assessed");
  const assessedCount = assessments.filter((a) =>
    (a.condition && a.condition !== "not_assessed") || isServiceAssessed(a)
  ).length;

  return (
    <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5 mb-4" data-testid="equipment-carousel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] flex items-center gap-2">
          <Wrench size={11} /> Customer Equipment · {machines.length}
        </div>
        <div className="text-[11px] text-[#8A8F98]" data-testid="assessment-progress">
          <span className={assessedCount === machines.length ? "text-[#39FF14]" : "text-yellow-300"}>{assessedCount}</span>
          <span className="text-[#8A8F98]"> / {machines.length} assessed</span>
        </div>
      </div>

      <div className="relative">
        <button type="button" onClick={() => scroll(-1)} data-testid="equipment-scroll-left"
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-8 h-8 rounded-full bg-[#0B0F0C] border border-[#3A4640] hover:border-[#39FF14]/50 flex items-center justify-center text-[#F8F9FA] shadow-lg">
          <ChevronLeft size={16} />
        </button>
        <button type="button" onClick={() => scroll(1)} data-testid="equipment-scroll-right"
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-8 h-8 rounded-full bg-[#0B0F0C] border border-[#3A4640] hover:border-[#39FF14]/50 flex items-center justify-center text-[#F8F9FA] shadow-lg">
          <ChevronRight size={16} />
        </button>

        <div ref={scrollerRef} className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1"
          style={{ scrollbarWidth: "thin" }}>
          {machines.map((m, i) => {
            const a = get(m.id);
            const isAssessed = (a.condition && a.condition !== "not_assessed") || isServiceAssessed(a);
            const open = openIdx === i;
            const label = `EQ-${String(i + 1).padStart(2, "0")}`;
            return (
              <div key={m.id} data-testid={`equipment-card-${i}`}
                className={`snap-start shrink-0 w-[220px] rounded-lg border transition-all ${isAssessed ? "border-[#39FF14]/40 bg-[#39FF14]/5" : "border-[#3A4640] bg-[#0B0F0C]"}`}>
                {/* 1:1 image area */}
                <div className="aspect-square rounded-t-lg overflow-hidden bg-[#0B0F0C]">
                  <ProductImageOrPlaceholder product={m.product} size="sm" />
                </div>
                <div className="px-3 py-2.5">
                  <div className="flex items-center justify-between gap-1">
                    <div className="text-[10px] font-mono text-[#39FF14] tracking-wider">{label}</div>
                    <span className={`text-[10px] font-heading tracking-wider uppercase px-1.5 py-0.5 rounded ${isAssessed ? "bg-[#39FF14] text-[#0B0F0C]" : "bg-[#3A4640] text-[#8A8F98]"}`}
                      data-testid={`equipment-status-${i}`}>
                      {isAssessed ? "Assessed" : "Unassessed"}
                    </span>
                  </div>
                  <div className="text-xs font-semibold text-[#F8F9FA] truncate mt-1" title={m.product?.name}>
                    {m.product?.brand || "—"} {m.product?.model || m.product?.name || "Machine"}
                  </div>
                  <div className="text-[10px] text-[#8A8F98] truncate">
                    {m.serial_number ? `S/N ${m.serial_number}` : m.purchased_at ? formatDate(m.purchased_at) : "—"}
                  </div>
                  <button type="button" disabled={disabled} onClick={() => setOpenIdx(open ? null : i)}
                    data-testid={`equipment-assess-btn-${i}`}
                    className="mt-2 w-full text-[11px] py-1.5 rounded border border-[#3A4640] hover:border-[#39FF14]/50 text-[#F8F9FA] bg-[#121815] flex items-center justify-center gap-1">
                    <ClipboardCheck size={11} /> {open ? "Close" : (isAssessed ? "Edit" : "Assess")}
                  </button>
                </div>

                {open && (
                  <div className="border-t border-[#3A4640] p-3 space-y-2.5" data-testid={`equipment-panel-${i}`}>
                    <div>
                      <div className="text-[10px] uppercase text-[#8A8F98] mb-1">Condition</div>
                      <div className="flex flex-wrap gap-1">
                        {MACHINE_CONDITIONS.map((c) => {
                          const on = a.condition === c.key;
                          return (
                            <button key={c.key} type="button" onClick={() => upsert(m.id, { condition: on ? "not_assessed" : c.key })}
                              data-testid={`cond-${i}-${c.key}`}
                              className={`text-[10px] px-2 py-1 rounded border ${on ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]" : TONE_BUTTON[c.tone]}`}>
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-[#8A8F98] mb-1">Service required (multi-select, independent of condition)</div>
                      <div className="flex flex-wrap gap-1">
                        {SERVICE_REQUIREMENTS.map((s) => {
                          const on = (a.service_requirements || []).includes(s.key);
                          return (
                            <button key={s.key} type="button" onClick={() => toggleService(m.id, s.key)}
                              data-testid={`serv-${i}-${s.key}`}
                              aria-pressed={on}
                              className={`text-[10px] px-2 py-1 rounded border ${on ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]" : TONE_BUTTON.neutral}`}>
                              {on ? "✓ " : ""}{s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <Textarea rows={2} value={a.notes || ""} placeholder="Notes for this machine…"
                      data-testid={`equipment-notes-${i}`}
                      onChange={(e) => upsert(m.id, { notes: e.target.value })} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function CallingDesk() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const [categoriesMeta, setCategoriesMeta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedOutcome, setPickedOutcome] = useState(null);
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("none");
  const [followupTitle, setFollowupTitle] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupPriority, setFollowupPriority] = useState("medium");
  const [interestSummary, setInterestSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const [showLockedInfo, setShowLockedInfo] = useState(null);

  // NEW — structured response state
  const [interestLevel, setInterestLevel] = useState("");
  const [purchaseTimeframe, setPurchaseTimeframe] = useState("");
  const [interestedCategories, setInterestedCategories] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [prevContact, setPrevContact] = useState(null);

  const resetForm = () => {
    setPickedOutcome(null); setNote(""); setNextAction("none");
    setFollowupTitle(""); setFollowupDate(""); setFollowupPriority("medium");
    setInterestSummary("");
    setInterestLevel(""); setPurchaseTimeframe(""); setInterestedCategories([]);
    setAssessments([]); setPrevContact(null);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: camp }, { data: outs }, { data: meta }] = await Promise.all([
        api.get(`/calling/campaigns/${id}`),
        api.get("/call-outcomes"),
        api.get("/products/master/meta").catch(() => ({ data: { categories: [] } })),
      ]);
      setCampaign(camp);
      setOutcomes(outs || []);
      setCategoriesMeta(meta.categories || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); navigate("/engagement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); resetForm(); /* eslint-disable-next-line */ }, [id]);

  const current = campaign?.next_customer;

  // Probe previous-contact warning whenever the current customer changes.
  useEffect(() => {
    if (!current?.id || !id) { setPrevContact(null); return; }
    api.get(`/calling/campaigns/${id}/customer/${current.id}/previous-contact`)
       .then((r) => setPrevContact(r.data))
       .catch(() => setPrevContact(null));
  }, [current?.id, id]);

  const toggleInterestedCat = (c) =>
    setInterestedCategories((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  const save = async () => {
    if (!current || !pickedOutcome) { toast.error("Pick a Contact Result outcome first"); return; }
    if (nextAction === "followup" && !followupDate) { toast.error("Pick a follow-up date"); return; }
    setSaving(true);
    try {
      // 1. Legacy interaction (still the transaction root — backward compat).
      const payload = {
        customer_id: current.id, campaign_id: campaign.id,
        outcome_key: pickedOutcome.key, note: note.trim(),
        next_action: nextAction,
      };
      if (nextAction === "followup") {
        payload.followup = { title: followupTitle.trim() || `Callback — ${current.name || ""}`.trim(),
          due_date: followupDate, notes: note.trim(), priority: followupPriority };
      }
      if (nextAction === "interest") payload.interest_summary = interestSummary.trim() || note.trim();
      const { data: inter } = await api.post("/calling/interactions", payload);

      // 2. Attach the structured extras (only if the caller filled anything).
      const hasStructured = interestLevel || purchaseTimeframe || interestedCategories.length > 0 || note.trim();
      const isServiceFilled = (a) => (a.service_requirements || []).some((r) => r && r !== "not_assessed");
      const filledAssessments = assessments.filter((a) =>
        (a.condition && a.condition !== "not_assessed") ||
        isServiceFilled(a) ||
        (a.notes || "").trim());
      if (hasStructured || filledAssessments.length) {
        await api.post("/calling/interactions/extras", {
          interaction_id: inter.id,
          structured_response: {
            contact_result_key: pickedOutcome.key,
            interest_level: interestLevel || null,
            purchase_timeframe: purchaseTimeframe || null,
            new_machine_categories: interestedCategories,
            notes: note.trim() || null,
          },
          machine_assessments: filledAssessments.map((a) => ({
            machine_id: a.machine_id,
            condition: a.condition || "not_assessed",
            service_requirements: (a.service_requirements && a.service_requirements.length)
              ? a.service_requirements
              : ["not_assessed"],
            notes: a.notes || "",
          })),
        });
      }
      toast.success("Interaction saved");
      resetForm();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save"); }
    finally { setSaving(false); }
  };

  const skip = async () => {
    if (!current) return;
    if (!window.confirm("Skip this customer without recording an outcome?")) return;
    try {
      await api.post(`/calling/campaigns/${id}/skip`, { customer_id: current.id });
      resetForm();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Skip failed"); }
  };

  const completeCampaign = async () => {
    if (!window.confirm("Mark this campaign as completed?")) return;
    try { await api.post(`/calling/campaigns/${id}/complete`); toast.success("Campaign completed"); navigate("/engagement"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (loading) return <SkeletonPage title kpi={false} table={false} />;
  if (!campaign) return null;

  const processed = (campaign.done_ids?.length || 0) + (campaign.skipped_ids?.length || 0);
  const pct = campaign.total ? Math.round((processed / campaign.total) * 100) : 0;

  return (
    <div className="p-6 md:p-8" data-testid="calling-desk">
      {/* Sticky top progress bar */}
      <div className="sticky top-[64px] z-20 -mx-6 md:-mx-8 mb-4 px-6 md:px-8 py-3 bg-[#121815]/95 backdrop-blur border-b border-[#3A4640]">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/engagement")} className="text-sm text-[#8A8F98] hover:text-[#F8F9FA] flex items-center gap-1" data-testid="back-to-engagement">
            <ArrowLeft size={14} /> All Campaigns
          </button>
          <div className="w-px h-4 bg-stone-300" />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-semibold text-[#F8F9FA] truncate">{campaign.name}</div>
            <div className="text-xs text-[#8A8F98]">{processed} of {campaign.total} processed · {campaign.total - processed} remaining</div>
          </div>
          <div className="w-56 h-1.5 bg-[#1A1F1C] rounded-full overflow-hidden">
            <div className="h-full bg-[#39FF14] rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {!current ? (
        <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-10 text-center">
          <CheckCircle2 size={40} className="mx-auto text-[#39FF14]" />
          <div className="mt-3 text-xl font-heading font-bold text-[#F8F9FA]">Campaign done!</div>
          <div className="text-sm text-[#8A8F98] mt-1">You've processed every customer in this list.</div>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="outline" onClick={() => navigate("/engagement")} data-testid="back-btn-empty">Back to campaigns</Button>
            {campaign.status !== "completed" && (
              <Button className="bg-[#39FF14] hover:bg-[#2ECC71]" onClick={completeCampaign} data-testid="complete-campaign-btn">
                <Check size={14} className="mr-2" /> Mark campaign completed
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr,380px] gap-4">
          {/* MAIN: customer + call form */}
          <div>
            <CustomerHeader c={current} />

            {/* Previous-contact warning banner (informational only) */}
            {prevContact?.warn && (
              <div className="mb-4 bg-yellow-500/10 border border-yellow-500/40 text-yellow-100 rounded-lg p-4 flex items-start gap-3" data-testid="previous-contact-warning">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-300" />
                <div className="text-sm">
                  <div className="font-heading font-bold">Previously contacted within the last {prevContact.days} day{prevContact.days === 1 ? "" : "s"}</div>
                  <div className="text-xs mt-0.5">
                    {formatDate(prevContact.last_called_at)} · {prevContact.last_outcome || "—"} · by {prevContact.last_caller || "—"}
                  </div>
                  <div className="text-[11px] text-yellow-200/80 mt-1">This is informational only — you can still make the call.</div>
                </div>
              </div>
            )}

            {/* Equipment carousel — square 1:1 cards, EQ-01/EQ-02, individual assessment */}
            <EquipmentAssessmentCarousel
              machines={current.machines || []}
              assessments={assessments}
              setAssessments={setAssessments}
              disabled={saving}
            />

            {/* Contact Result (mandatory — mirrors legacy outcome_key) */}
            <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5 mb-4">
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-2">Contact Result <span className="text-red-500">*</span></div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="outcome-picker">
                {outcomes.map((o) => {
                  const active = pickedOutcome?.key === o.key;
                  return (
                    <button key={o.key} type="button" onClick={() => setPickedOutcome(o)}
                      data-testid={`outcome-${o.key}`}
                      className={`text-sm text-left px-3 py-2.5 rounded-md border transition-colors ${active ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]/40" : TONE_CLS[o.tone] || TONE_CLS.neutral}`}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* New Machine Interest — customer interest & timeframe */}
            <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5 mb-4" data-testid="structured-interest-section">
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-3 flex items-center gap-2"><ShoppingCart size={11} /> New Machine Interest</div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px]">Customer Interest Level</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {INTEREST_LEVELS.map((l) => {
                      const on = interestLevel === l.key;
                      return (
                        <button key={l.key} type="button" onClick={() => setInterestLevel(on ? "" : l.key)}
                          data-testid={`interest-level-${l.key}`}
                          className={`text-xs px-2.5 py-1.5 rounded border ${on ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]" : TONE_BUTTON.neutral}`}>
                          {l.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Purchase Timeframe</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {PURCHASE_TIMEFRAMES.map((t) => {
                      const on = purchaseTimeframe === t.key;
                      return (
                        <button key={t.key} type="button" onClick={() => setPurchaseTimeframe(on ? "" : t.key)}
                          data-testid={`timeframe-${t.key}`}
                          className={`text-xs px-2.5 py-1.5 rounded border ${on ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]" : TONE_BUTTON.neutral}`}>
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              {categoriesMeta.length > 0 && (
                <div className="mt-4">
                  <Label className="text-[11px]">Product Category Interest</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="category-interest-picker">
                    {categoriesMeta.map((c) => {
                      const on = interestedCategories.includes(c);
                      return (
                        <button key={c} type="button" onClick={() => toggleInterestedCat(c)}
                          data-testid={`cat-int-${c}`}
                          className={`text-xs px-2.5 py-1.5 rounded border ${on ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]" : TONE_BUTTON.neutral}`}>
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5 mb-4">
              <Label>Notes</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                rows={3} placeholder="What did the customer say? Any specific concerns…" data-testid="call-note-input" />
            </div>

            {/* Next action */}
            <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5 mb-4">
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-2">Next Action</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {[
                  { key: "none",     label: "No further action",      icon: Check },
                  { key: "followup", label: "Follow up later",        icon: Calendar },
                  { key: "interest", label: "Record customer interest", icon: Sparkles },
                ].map(({ key, label, icon: Icon }) => (
                  <button key={key} type="button" onClick={() => setNextAction(key)}
                    data-testid={`next-action-${key}`}
                    className={`text-sm px-3 py-2 rounded-md border transition-colors flex items-center gap-2 ${nextAction === key ? "bg-yellow-500 text-[#0B0F0C] border-yellow-500/40" : "bg-[#121815] text-[#F8F9FA] border-[#3A4640] hover:bg-[#121815]"}`}>
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>

              {/* Locked actions row (informational) */}
              <div className="flex flex-wrap gap-2 border-t border-[#3A4640] pt-3">
                {LOCKED_ACTIONS.map((la) => (
                  <button key={la.key} type="button" onClick={() => setShowLockedInfo(la)}
                    data-testid={`locked-action-${la.key}`}
                    className="text-xs px-2.5 py-1.5 rounded-md border bg-[#0B0F0C] text-[#8A8F98] border-[#3A4640] hover:bg-[#121815] flex items-center gap-1.5">
                    <Lock size={11} /> {la.label} — Coming soon
                  </button>
                ))}
              </div>

              {/* Inline detail forms */}
              {nextAction === "followup" && (
                <div className="mt-4 grid md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label>Follow-up title</Label>
                    <Input value={followupTitle} onChange={(e) => setFollowupTitle(e.target.value)} placeholder={`Callback — ${current.name || ""}`} data-testid="followup-title-input" />
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={followupPriority} onValueChange={setFollowupPriority}>
                      <SelectTrigger data-testid="followup-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label>Due date</Label>
                    <Input type="datetime-local" value={followupDate} onChange={(e) => setFollowupDate(e.target.value)} data-testid="followup-date-input" />
                  </div>
                </div>
              )}

              {nextAction === "interest" && (
                <div className="mt-4">
                  <Label>Interest summary</Label>
                  <Textarea rows={2} value={interestSummary} onChange={(e) => setInterestSummary(e.target.value)} placeholder="What is the customer interested in?" data-testid="interest-summary-input" />
                  <div className="text-xs text-[#8A8F98] mt-1">Saved with the interaction. A dedicated Leads module will consume these in a future release.</div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button className="bg-[#39FF14] hover:bg-[#2ECC71]" onClick={save} disabled={saving || !pickedOutcome} data-testid="save-interaction-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Check size={14} className="mr-2" />}
                Save & Next
              </Button>
              <Button variant="outline" onClick={skip} data-testid="skip-customer-btn">
                <PhoneOff size={14} className="mr-2" /> Skip
              </Button>
              <Button variant="ghost" onClick={completeCampaign} className="ml-auto text-[#8A8F98] hover:text-red-300" data-testid="complete-campaign-early-btn">
                End campaign
              </Button>
            </div>
          </div>

          {/* SIDE: context */}
          <aside className="space-y-4">
            <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4">
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-1.5 flex items-center gap-1"><Package size={11} /> Machines Owned</div>
              <MachinesPanel machines={current.machines} />
            </div>
            <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4">
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-1.5 flex items-center gap-1"><PhoneCall size={11} /> Last Interaction</div>
              <LastInteractionPanel last={current.last_interaction} />
            </div>
            <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4">
              <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-1.5 flex items-center gap-1"><Calendar size={11} /> Last Follow-up</div>
              <LastFollowupPanel f={current.last_followup} />
            </div>
            {current.notes && (
              <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-4">
                <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-1.5 flex items-center gap-1"><ClipboardList size={11} /> Notes</div>
                <div className="text-sm text-[#F8F9FA] italic whitespace-pre-wrap">&ldquo;{current.notes}&rdquo;</div>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* Locked-action explainer */}
      {showLockedInfo && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0B0F0C]/60 backdrop-blur-sm p-4" onClick={() => setShowLockedInfo(null)}>
          <div className="bg-[#121815] rounded-lg shadow-2xl border border-[#3A4640] max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded bg-[#121815] flex items-center justify-center"><Lock size={16} className="text-[#8A8F98]" /></div>
              <div>
                <div className="font-heading font-bold text-[#F8F9FA]">{showLockedInfo.label}</div>
                <div className="text-xs text-[#8A8F98]">Coming soon</div>
              </div>
            </div>
            <p className="text-sm text-[#F8F9FA] mt-3">{showLockedInfo.reason}. Save the interaction now — this action will be enabled in a future release. Your call outcome and note are never lost.</p>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setShowLockedInfo(null)}>Got it</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
